import { create } from 'zustand';
import { createEntityStore } from '@redpanda/utils';
import { customerApi } from '../services/api';
import toast from 'react-hot-toast';
import i18n from '../i18n';

export interface Customer {
  id: string;
  phone: string;
  name: string;
  email?: string;
  avatar?: string;
  vipLevel?: string;
  totalSpent: number;
  visitCount: number;
  lastVisit?: string;
  tags: string[];
  note?: string;
  createdAt: string;
}

export interface CustomerDetail extends Customer {
  bookings: Array<{
    id: string;
    date: string;
    service: {
      name: string;
    };
    staff?: {
      name: string;
    } | null;
    price: number;
    status: string;
  }>;
}

// 創建基礎的客戶 store (使用 createEntityStore 獲得分頁功能)
export const useCustomerStoreBase = createEntityStore<Customer>({
  entityName: 'customer',
  fetchFn: (page: number) => customerApi.getPaginated(page, 20),
  deleteFn: undefined, // 客戶管理暫不提供刪除功能
  toast,
  messages: {
    fetchError: i18n.t('common.messages.fetchFailed'),
  },
});

// 擴展 store 以添加客戶特有的功能
interface ExtendedCustomerState {
  // 客戶詳情快取
  customerDetails: Record<string, CustomerDetail>;
  isLoadingDetail: boolean;
  fetchCustomerDetail: (customerId: string) => Promise<void>;

  // 更新客戶
  updateCustomer: (customerId: string, data: any) => Promise<void>;
}

export const useCustomerStore = create<ExtendedCustomerState>((set, get) => ({
  customerDetails: {},
  isLoadingDetail: false,

  fetchCustomerDetail: async (customerId: string) => {
    const { customerDetails } = get();

    // 如果已經有快取，先不設置 loading（背景更新）
    const hasCachedDetail = !!customerDetails[customerId];

    if (!hasCachedDetail) {
      set({ isLoadingDetail: true });
    }

    try {
      const response = await customerApi.get(customerId);
      const detail = response.data;

      set((state) => ({
        customerDetails: {
          ...state.customerDetails,
          [customerId]: detail,
        },
        isLoadingDetail: false,
      }));
    } catch (error: any) {
      console.error('Failed to fetch customer detail:', error);
      set({ isLoadingDetail: false });
      if (!hasCachedDetail) {
        toast.error(i18n.t('common.messages.fetchFailed'));
      }
    }
  },

  updateCustomer: async (customerId: string, data: any) => {
    // 1. 保存舊值
    const baseStore = useCustomerStoreBase.getState();
    const originalItems = [...baseStore.items];

    // 2. 樂觀更新客戶列表
    baseStore.updateItem(customerId, data);

    try {
      await customerApi.update(customerId, data);

      // 清除快取的詳情，強制重新載入
      set((state) => {
        const newDetails = { ...state.customerDetails };
        delete newDetails[customerId];
        return { customerDetails: newDetails };
      });

      toast.success(i18n.t('common.messages.updateSuccess'));
    } catch (error: any) {
      // 3. 失敗回滾
      useCustomerStoreBase.setState({ items: originalItems });
      console.error('Failed to update customer:', error);
      const errorMsg = error.response?.data?.message || i18n.t('common.messages.updateFailed');
      toast.error(errorMsg);
      throw error;
    }
  },
}));

// 提供統一的 hook 來訪問所有功能
export const useCustomers = () => {
  const baseStore = useCustomerStoreBase();
  const extendedStore = useCustomerStore();

  return {
    // 基礎的分頁功能 (來自 createEntityStore)
    customers: baseStore.items,
    pagination: baseStore.pagination,
    currentPage: baseStore.currentPage,
    error: baseStore.error,
    isLoading: baseStore.isLoading,
    isPageLoading: baseStore.isPageLoading,
    fetchCustomers: baseStore.fetchItems,
    setCurrentPage: baseStore.setCurrentPage,
    refreshData: baseStore.refreshData,

    // 客戶詳情功能
    customerDetails: extendedStore.customerDetails,
    isLoadingDetail: extendedStore.isLoadingDetail,
    fetchCustomerDetail: extendedStore.fetchCustomerDetail,

    // 更新功能
    updateCustomer: extendedStore.updateCustomer,
  };
};
